<template>
    <div class="ai-summary">
        <div class="ai-summary-header">
            <div class="ai-summary-eyebrow">Draft ready</div>
            <h3 v-if="summary.name" class="ai-summary-title">
                {{ summary.name }}<span v-if="summary.company"> · {{ summary.company }}</span>
            </h3>
            <h3 v-else class="ai-summary-title">Quote draft</h3>
            <div v-if="summary.quote_id" class="ai-summary-id">Quote #{{ summary.quote_id }}</div>
        </div>

        <!-- Standard (non-tiered) totals -->
        <div v-if="!summary.tiered_quote" class="ai-summary-body">
            <div class="ai-summary-total">
                <span class="ai-summary-total-label">Total inc VAT</span>
                <span class="ai-summary-total-amount">{{ formatGbp(totals.total_price) }}</span>
            </div>

            <div class="ai-summary-totals-grid">
                <div v-if="totals.sub_total"><span>Sub-total</span><span>{{ formatGbp(totals.sub_total) }}</span></div>
                <div v-if="totals.delivery_price"><span>Delivery</span><span>{{ formatGbp(totals.delivery_price) }}</span></div>
                <div v-if="totals.fitting_price"><span>Fitting</span><span>{{ formatGbp(totals.fitting_price) }}</span></div>
                <div v-if="totals.discount_price"><span>Discount</span><span>−{{ formatGbp(totals.discount_price) }}</span></div>
                <div v-if="totals.vat_price"><span>VAT</span><span>{{ formatGbp(totals.vat_price) }}</span></div>
            </div>
        </div>

        <!-- Tiered alternatives -->
        <div v-else class="ai-summary-body">
            <div class="ai-summary-tier-note">
                <i class="fa fa-info-circle"></i>
                Tiered quote: the customer picks one option, not all of them.
            </div>
            <div class="ai-summary-tier-grid">
                <div v-for="opt in summary.per_option_totals" :key="opt.item_id" class="ai-summary-tier">
                    <div class="ai-summary-tier-name">{{ opt.name }}</div>
                    <div class="ai-summary-tier-price">{{ formatGbp(opt.total_inc_vat) }}</div>
                    <div class="ai-summary-tier-meta">
                        <span v-if="opt.delivery_each">+ {{ formatGbp(opt.delivery_each) }} delivery</span>
                        <span v-if="opt.fitting_each">+ {{ formatGbp(opt.fitting_each) }} fitting</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Items -->
        <ul v-if="summary.items && summary.items.length" class="ai-summary-items">
            <li v-for="i in summary.items" :key="i.id">
                <span class="ai-summary-item-name">{{ i.name }}</span>
                <span v-if="i.quantity > 1" class="ai-summary-item-qty">×{{ i.quantity }}</span>
                <span class="ai-summary-item-price">{{ formatGbp(i.price) }}</span>
            </li>
        </ul>

        <!-- Actions -->
        <div class="ai-summary-actions">
            <button
                type="button"
                class="ai-draft-btn ai-draft-btn-primary"
                :disabled="!effectiveQuoteId"
                @click="$emit('action', { action: 'download_pdf', quoteId: effectiveQuoteId })"
            >
                <i class="fa fa-file-pdf"></i>
                Download PDF
            </button>
            <button
                type="button"
                class="ai-draft-btn ai-draft-btn-secondary"
                :disabled="!effectiveQuoteId"
                @click="$emit('action', { action: 'open_in_editor', quoteId: effectiveQuoteId })"
            >
                <i class="fa fa-edit"></i>
                Open in editor
            </button>
            <button
                type="button"
                class="ai-draft-btn ai-draft-btn-ghost"
                @click="$emit('action', { action: 'cheaper' })"
            >
                <i class="fa fa-arrow-down"></i>
                Make it cheaper
            </button>
            <button
                type="button"
                class="ai-draft-btn ai-draft-btn-ghost"
                @click="$emit('action', { action: 'tweak' })"
            >
                <i class="fa fa-sliders-h"></i>
                Tweak something
            </button>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        summary: { type: Object, required: true },
        quoteId: { type: Number, default: null },
    },

    computed: {
        totals() {
            return this.summary.totals || {};
        },
        effectiveQuoteId() {
            return this.summary.quote_id || this.quoteId || null;
        },
    },

    methods: {
        formatGbp(pence) {
            if (pence === null || pence === undefined) return '';
            const pounds = pence / 100;
            return '£' + pounds.toLocaleString('en-GB', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
    },
};
</script>

<style>
.ai-summary {
    margin-top: 12px;
    background: linear-gradient(180deg, #fff 0%, #fafbfc 100%);
    border: 1px solid var(--ai-border);
    border-radius: var(--ai-radius);
    padding: 18px 20px;
    box-shadow: var(--ai-shadow);
}

.ai-summary-header {
    margin-bottom: 14px;
}

.ai-summary-eyebrow {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .08em;
    color: var(--ai-primary);
    background: var(--ai-user-tint);
    padding: 2px 8px;
    border-radius: 999px;
    margin-bottom: 6px;
}

.ai-summary-title {
    font-size: 17px;
    font-weight: 600;
    margin: 0 0 2px;
    color: var(--ai-text);
}

.ai-summary-id {
    font-size: 12px;
    color: var(--ai-muted);
}

.ai-summary-body {
    padding: 12px 0;
    border-top: 1px solid var(--ai-border);
    border-bottom: 1px solid var(--ai-border);
}

.ai-summary-total {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 10px;
}

.ai-summary-total-label {
    font-size: 13px;
    color: var(--ai-muted);
    text-transform: uppercase;
    letter-spacing: .05em;
}

.ai-summary-total-amount {
    font-size: 28px;
    font-weight: 700;
    color: var(--ai-text);
    letter-spacing: -0.02em;
}

.ai-summary-totals-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 6px 16px;
    font-size: 13px;
    color: var(--ai-muted);
}

.ai-summary-totals-grid > div {
    display: flex;
    justify-content: space-between;
}

/* Tiered */

.ai-summary-tier-note {
    background: var(--ai-user-tint);
    color: var(--ai-primary);
    padding: 8px 12px;
    border-radius: var(--ai-radius-sm);
    font-size: 13px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.ai-summary-tier-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
}

.ai-summary-tier {
    border: 1px solid var(--ai-border);
    border-radius: var(--ai-radius-sm);
    padding: 12px;
    background: #fff;
}

.ai-summary-tier-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--ai-text);
    margin-bottom: 4px;
}

.ai-summary-tier-price {
    font-size: 20px;
    font-weight: 700;
    color: var(--ai-primary);
    margin-bottom: 4px;
}

.ai-summary-tier-meta {
    font-size: 11px;
    color: var(--ai-muted);
    display: flex;
    flex-direction: column;
    gap: 2px;
}

/* Items */

.ai-summary-items {
    list-style: none;
    margin: 12px 0 0;
    padding: 0;
}

.ai-summary-items li {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    padding: 4px 0;
}

.ai-summary-item-name {
    flex: 1;
    color: var(--ai-text);
}

.ai-summary-item-qty {
    color: var(--ai-muted);
    font-size: 12px;
}

.ai-summary-item-price {
    color: var(--ai-text);
    font-variant-numeric: tabular-nums;
}

/* Actions */

.ai-summary-actions {
    display: flex;
    gap: 8px;
    margin-top: 16px;
    flex-wrap: wrap;
}

@media (max-width: 600px) {
    .ai-summary-actions .ai-draft-btn {
        flex: 1 1 calc(50% - 4px);
        justify-content: center;
    }
}
</style>
